import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Box, Typography, Paper, Chip } from "@mui/material";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FormResponseTable from "../FormResponse/FormResponseTable";
import MyFormFilterDrawer from "../MyForm/MyFormFilterDrawer";
import NoDataAvailable from "../../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../../components/Loader/Loader";
import IconButtons from "../../../components/IconButtons/IconButtons";
import UserTableAvatar from "../../../components/UserTableAvatar/UserTableAvatar";

import { font_15, getStatus } from "../../../utils/utils";
import { filter_active_icon } from "../../../constants/icons";
import { HelpTexts } from "../../../constants/constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BootstrapTooltip from "../../../components/BootstrapTooltip/BootstrapTooltip";

import {
  clearFormResponse,
  fetchMentorFeedbacks,
  fetchResponseListingFilterOptionsForMentor,
} from "../../../store/formResponseSlice";

dayjs.extend(utc);
dayjs.extend(tz);

const { NON_ACTIVE_ATTENDEE } = HelpTexts;

const statusConfig = {
  statuses: [
    { id: 0, title: "Active", value: "ACTIVE", variant: "GREEN-TEXT" },
    { id: 1, title: "Inactive", value: "INACTIVE", variant: "RED-TEXT" },
    { id: 3, title: "Completed", value: "COMPLETED", variant: "GREEN-TEXT" },
    { id: 4, title: "Pending", value: "PENDING", variant: "RED-TEXT" },
  ],
  getVariant: (value) => statusConfig.statuses.find((it) => it.value === value)?.variant || "",
  getTitle: (value) => statusConfig.statuses.find((it) => it.value === value)?.title || "",
};

const getScoreClass = (val) => {
  const score = parseFloat(val);
  if (isNaN(score)) return null;
  if (score >= 0 && score <= 2.5) return "score-chip chip-red";
  if (score > 2.5 && score < 3.8) return "score-chip chip-yellow";
  if (score >= 3.8) return "score-chip chip-green";
  return "score-chip chip-default";
};

const formatDate = (date) => {
  if (!date) return "-";
  const parsedDate = new Date(date);
  return isNaN(parsedDate.getTime()) ? "-" : parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });
};

const extractUserData = (user = {}) => ({
  newerId: user.employeeCode || "",
  fullName: user.fullName || "",
  profilePictureUrl: user.profilePictureUrl || "",
  newerEmail: user.email || "",
  userDeleted: user.deleted || false,
  competency: user.competency?.name || "",
  mentorFullName: user.mentor?.fullName || "",
  mentorEmail: user.mentor?.email || "",
  mentorId: user.mentor?.employeeCode,
  reportingManagerFullName: user.reportingManager?.fullName || "",
  reportingManagerEmail: user.reportingManager?.email || "",
  reportingManagerId: user.reportingManager?.employeeCode,
});

const mapApiToTableRows = (apiResponse = []) =>
  apiResponse.map((item) => ({
    ...extractUserData(item.filledForUser),
    dateOfAssessment: formatDate(item.trigger?.assessmentDate),
    assessmentPeriod: `${formatDate(item.trigger?.periodFrom)} - ${formatDate(item.trigger?.periodTo)}`,
    assessmentName: item.trigger?.assessmentName,
    bootcamp: item.trigger?.bootcamp?.name,
    status: item.status,
    evaluatedBy: item.filledByUser?.fullName || "",
    ...item.inputResponses?.results?.reduce((acc, input) => {
      const label = input?.input?.label || "Unknown";
      acc[label] = { value: input?.value || "-", tooltip: input?.input?.description || "" };
      return acc;
    }, {}),
    original: item,
  }));

const MyFormAsMentor = () => {
  const dispatch = useDispatch();
  const isInitialized = useRef(false);

  const { list: apiData = [], loading = false, totalResults = 0 } = useSelector((state) => state.formResponse || {});
  const attendeeStatuses = useSelector((state) => state?.utility?.constants?.attendeeStatuses);
  const security = useSelector((state) => state?.security);
  const [openFilter, setOpenFilter] = useState(false);
  const [formNameOptions, setFormNameOptions] = useState([]);
  const [isFilter, setIsFilter] = useState({ formName: null });
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFormType, setSelectedFormType] = useState("");

  const isMentor = security?.loggedInUser?.permission?.mentor === true;

  const applyFilter = useCallback((filter = isFilter, pageOffset = offset) => {
    if (!filter?.formName?.id) return;
    dispatch(clearFormResponse());
    dispatch(fetchMentorFeedbacks({
      formId: filter.formName.id,
      offset: pageOffset,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const loadOptions = async () => {
      const resultAction = await dispatch(fetchResponseListingFilterOptionsForMentor()).unwrap();
      const forms = resultAction?.form?.results || [];
      const options = forms.map(f => ({ id: f.id, name: f.label, type: f.type, description: f.description }));
      setFormNameOptions(options);

      if (options.length) {
        const defaultFilter = { formName: options[0] };
        setIsFilter(defaultFilter);
        setSelectedFormType(options[0].type);
        applyFilter(defaultFilter, 0);
      }
    };
    loadOptions();

    return () => dispatch(clearFormResponse());
  }, [dispatch]);

  const rows = useMemo(() => {
    const data = Array.isArray(apiData) ? apiData : apiData?.results || [];
    return mapApiToTableRows(data);
  }, [apiData]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: "fullName",
        label: "Attendee",
        render: (val, row) => (
          <UserTableAvatar
            imgSrc={row.profilePictureUrl}
            label={row.fullName}
            subLabel={row.newerEmail}
            deleted={row.userDeleted}
            userId={row.newerId}
          />
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (val) => (
          <Chip
            variant={statusConfig.getVariant(val)}
            label={statusConfig.getTitle(val)}
            size="small"
          />
        ),
      },
      { key: "bootcamp", label: "Bootcamp" },
    ];

    if (selectedFormType === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
      baseColumns.push({ key: "assessmentName", label: "Assessment" }, { key: "dateOfAssessment", label: "Date" });
    } else {
      baseColumns.push({ key: "assessmentPeriod", label: "Period" });
    }

    const dynamicCols = [];
    const firstItemResults = (Array.isArray(apiData) ? apiData : apiData?.results)?.[0]?.inputResponses?.results || [];

    firstItemResults.forEach(input => {
      const label = input?.input?.label;
      if (!label) return;
      dynamicCols.push({
        key: label,
        label: label,
        tooltip: input?.input?.description || "",
        render: (cell) => {
          if (input?.input?.type === "AVERAGE_SCORE" && cell?.value !== "-") {
            return <Chip label={cell.value} size="small" className={getScoreClass(cell.value)} />;
          }
          return <Box title={cell?.tooltip || ""}>{cell?.value || "-"}</Box>;
        }
      });
    });

    return [...baseColumns, ...dynamicCols];
  }, [apiData, selectedFormType]);

  const handleFilterSubmit = () => {
    setOffset(0);
    setCurrentPage(1);
    setOpenFilter(false);
    if (isFilter?.formName) {
      setSelectedFormType(isFilter.formName.type);
      applyFilter(isFilter, 0);
    }
  };

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * 20;
    setCurrentPage(page);
    setOffset(newOffset);
    applyFilter(isFilter, newOffset);
  };

  if (!isMentor) {
    return <Typography sx={{ p: 3 }}>You do not have mentor permissions to view this page.</Typography>;
  }

  return (
    <>
      {loading && <Loader />}
           <Grid container className="main-container">
        
        <Grid 
          item 
          md={12} 
          className="header-container" 
          sx={{ 
            height: "auto !important",
            minHeight: "60px",
          }}
        >        
          <Box
            display="flex"
            alignItems="flex-start" 
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="font_21_bold">
                {isFilter?.formName?.name || "Mentor Feedbacks"}
              </Typography>

              {isFilter?.formName?.description && (
                <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ mt: 0.2 }}>
                  <InfoOutlinedIcon 
                    sx={{ 
                      fontSize: "1rem", 
                      color: "#de1186", 
                      mt: "3px",
                      flexShrink: 0
                    }} 
                  />
                  <Typography
                    variant="font_12"
                    component="div"
                    sx={{ 
                      color: "#4b4b4b", 
                      fontStyle: "italic",
                      wordBreak: "break-word",
                      "& p": { margin: 0 } 
                    }}
                    dangerouslySetInnerHTML={{
                      __html: isFilter.formName.description,
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="center" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
              <IconButtons
                height={font_15}
                width={font_15}
                image={filter_active_icon}
                handleClick={() => setOpenFilter(true)}
                count={isFilter.formName ? 1 : 0}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item md={12} className="body-container">
          <Grid container className="table-parent-container">
            <Paper elevation={0} className="flex-container-column topic-paper-container">
              {!rows.length && !loading ? (
                <NoDataAvailable message="No Mentor Feedbacks Found" />
              ) : (
                <FormResponseTable
                  title="Mentor Feedbacks"
                  columns={columns}
                  rows={rows}
                  totalResult={totalResults}
                  totalPages={Math.ceil(totalResults / 20)}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <MyFormFilterDrawer
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        handleFilterChange={(name, val) => setIsFilter(prev => ({ ...prev, [name]: val }))}
        handleFilterSubmit={handleFilterSubmit}
        handleFilterRemove={() => {
          const first = formNameOptions[0];
          setIsFilter({ formName: first });
          setSelectedFormType(first?.type);
          applyFilter({ formName: first }, 0);
          setOpenFilter(false);
        }}
        filterValue={isFilter}
        formNameOptions={formNameOptions}
        hideStatusSelect={true}
      />
    </>
  );
};

export default MyFormAsMentor;